import React from 'react'
import { connect, useDispatch, useSelector } from 'react-redux'

import { selectHouseAndLand, setByFlag } from '@store/actionSlices/houseAndLand'
import { initialFilter } from '@store/actionSlices/lotFilter'
import { ShortlistState } from '@store/actionSlices/shortlist'
import {
  LotFilterInterface,
  LotShortlistInterface,
  RootStateTypeExtra,
} from '@store/types'

import {
  HouseAndLandActiveKeyType,
  PackageInterface,
  StageInterface,
} from '@api/types/house-and-land-type'

import countFilterChanges from '@utilities/lot-filter-util'

import { useCompareProperty } from '@adUtilities/components/compare-property/utils'

import ActionsButton from './actions-button'
import AddFilterButton from './add-filter-button'
import BackButton from './back-button'
import LotCard from './lot-card'
import { LotCardProps } from './lot-card/lot-card'
import LotSidePanelSkeleton from './lot-side-panel-skeleton'
import NoLotCard from './no-lot-card'
import PrecinctDropdown from './precinct-dropdown'
import StageHead from './stage-head'

interface LotsSidePanelProps {
  isLoaded: boolean
  stages: Array<StageInterface>
  packages: Array<PackageInterface>
  filterPopupToggle: boolean
  setFilterPopupToggle: (arg: boolean) => void
  activeStageId: string
  activeLotId: string
  lotFilter: LotFilterInterface
  shortlists: ShortlistState['shortlists']
  isFetchingStages: boolean
  precinctLength: number
  onLotConfigurationValueChange?: LotCardProps['onConfigurationValueChange']
  onLotConfigurationOptionSelect?: LotCardProps['onConfigurationOptionSelect']
  onLotConfigurationPropertyExpand?: LotCardProps['onConfigurationPropertyExpand']
  comparePropertyProps: ReturnType<typeof useCompareProperty>
}

const PADDING_FROM_CONTAINER_TOP = 124

function LotsSidePanel({
  isLoaded,
  stages,
  packages,
  filterPopupToggle,
  setFilterPopupToggle,
  activeStageId,
  activeLotId,
  lotFilter,
  shortlists,
  isFetchingStages,
  precinctLength,
  onLotConfigurationValueChange,
  onLotConfigurationOptionSelect,
  onLotConfigurationPropertyExpand,
  comparePropertyProps,
}: LotsSidePanelProps) {
  const dispatch = useDispatch()
  const { activePackageId } = useSelector(selectHouseAndLand)
  const initialActivePackageIdRef = React.useRef<string | null>(activePackageId)
  const lotSidePanelContainerRef = React.useRef<HTMLDivElement>(null)
  const handleInitialActivePackageIdRef = React.useRef(true)

  const { packages: lotFilterPackages } = lotFilter

  const orderedStages = React.useMemo(
    () =>
      [...stages]?.sort?.(
        (a, b) => (a.order ?? Infinity) - (b.order ?? Infinity)
      ),
    [stages]
  )

  const packagesOfLot = React.useCallback(
    (lotId: string) =>
      packages.filter((item: PackageInterface) => item.lots.includes(lotId)),
    [packages]
  )

  const filteredCount = React.useMemo(() => {
    if (lotFilter.apply) {
      return countFilterChanges(initialFilter, lotFilter)
    }
    return 0
  }, [lotFilter.apply])

  const { defaultPackageId, validInitialActivePackageId } =
    React.useMemo(() => {
      const lotPackages = packagesOfLot(activeLotId)

      const filterSet = new Set(lotFilterPackages)
      const filterSelectedPackage = lotPackages.find((pkg) =>
        filterSet.has(pkg.name.toLowerCase())
      )
      const filterSelectedPackageId = filterSelectedPackage?.id || ''

      const initialActivePackage = lotPackages?.find(
        (pkg) => pkg.id === initialActivePackageIdRef.current
      )?.id

      return {
        defaultPackageId: filterSelectedPackageId || lotPackages?.[0]?.id,
        validInitialActivePackageId:
          filterSelectedPackageId || initialActivePackage,
      }
    }, [activeLotId, packagesOfLot, lotFilterPackages])

  const { isShortlisted, shortlistedConfiguration } = React.useMemo(() => {
    const matchedShortlist = shortlists.find(
      (item) => item.propertyId === activeLotId
    ) as LotShortlistInterface | undefined
    return {
      isShortlisted: !!matchedShortlist,
      shortlistedConfiguration: matchedShortlist?.configuration,
    }
  }, [shortlists, activeLotId])

  const lastCompareModeIsActiveRef = React.useRef(
    comparePropertyProps.compareModeIsActive
  )

  React.useEffect(() => {
    if (lastCompareModeIsActiveRef.current) {
      return
    }
    if (!activeLotId) {
      dispatch(
        setByFlag({
          flag: HouseAndLandActiveKeyType.ActivePackageId,
          value: '',
        })
      )
      return
    }
    if (
      initialActivePackageIdRef.current &&
      handleInitialActivePackageIdRef.current &&
      validInitialActivePackageId
    ) {
      dispatch(
        setByFlag({
          flag: HouseAndLandActiveKeyType.ActivePackageId,
          value: validInitialActivePackageId,
        })
      )
      handleInitialActivePackageIdRef.current = false
      return
    }
    dispatch(
      setByFlag({
        flag: HouseAndLandActiveKeyType.ActivePackageId,
        value:
          (isShortlisted
            ? shortlistedConfiguration?.floorplan
            : defaultPackageId) || '',
      })
    )
  }, [activeLotId, defaultPackageId, validInitialActivePackageId])

  React.useEffect(() => {
    lastCompareModeIsActiveRef.current =
      comparePropertyProps.compareModeIsActive
  }, [comparePropertyProps.compareModeIsActive])

  React.useEffect(() => {
    const timer = setTimeout(() => {
      const activeStageElement = document.querySelector(
        '.active-stage'
      ) as HTMLDivElement

      if (activeStageElement && lotSidePanelContainerRef?.current) {
        const offsetTop =
          activeStageElement.offsetTop -
          lotSidePanelContainerRef.current.offsetTop

        lotSidePanelContainerRef.current.scrollTo({
          top: offsetTop,
          behavior: 'smooth',
        })
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [activeStageId, lotSidePanelContainerRef.current])

  React.useEffect(() => {
    const timer = setTimeout(() => {
      const activeLotElement = document.getElementById(activeLotId)
      if (activeLotElement && lotSidePanelContainerRef?.current) {
        lotSidePanelContainerRef.current.scrollTo({
          top: activeLotElement.offsetTop - PADDING_FROM_CONTAINER_TOP,
          behavior: 'smooth',
        })
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [activeLotId, lotSidePanelContainerRef.current])

  return (
    <div className="h-full w-full bg-[#EEEEEE]">
      <div className="flex flex-col gap-4 bg-white p-4">
        <div className="flex justify-between gap-4">
          <div className="flex items-center space-x-4">
            <BackButton
              activeLotId={activeLotId}
              activeStageId={activeStageId}
              comparePropertyProps={comparePropertyProps}
            />
            <ActionsButton disable={!isLoaded} />
          </div>
          <AddFilterButton
            disable={!isLoaded}
            filterCount={filteredCount}
            popupState={filterPopupToggle}
            togglePopup={setFilterPopupToggle}
          />
        </div>

        <PrecinctDropdown />
      </div>
      {!isLoaded || isFetchingStages ? (
        <LotSidePanelSkeleton />
      ) : (
        <div
          ref={lotSidePanelContainerRef}
          className="house-and-land-scrollbar h-unit-sidepanel-container overflow-y-scroll px-4 pb-0.5"
          style={
            precinctLength > 1 ? { height: 'calc(100vh - 162px)' } : undefined
          }
        >
          {orderedStages.length > 0 ? (
            orderedStages.map((stage) => (
              <div
                key={stage.id}
                className={`${
                  stage.label === activeStageId ? 'active-stage' : ''
                }`}
              >
                <StageHead
                  stage={stage}
                  active={stage.label === activeStageId}
                />
                <div className="flex flex-col space-y-3">
                  {stage.lots.length > 0 ? (
                    stage.lots.map((lot) => (
                      <LotCard
                        key={lot.id}
                        lot={lot}
                        stageInfo={stage}
                        active={activeLotId === lot.id}
                        packages={packagesOfLot(lot.id)}
                        onConfigurationValueChange={
                          activeLotId === lot.id
                            ? onLotConfigurationValueChange
                            : undefined
                        }
                        onConfigurationOptionSelect={
                          activeLotId === lot.id
                            ? onLotConfigurationOptionSelect
                            : undefined
                        }
                        onConfigurationPropertyExpand={
                          activeLotId === lot.id
                            ? onLotConfigurationPropertyExpand
                            : undefined
                        }
                        comparePropertyProps={comparePropertyProps}
                      />
                    ))
                  ) : (
                    <NoLotCard />
                  )}
                </div>
              </div>
            ))
          ) : (
            <NoLotCard />
          )}
        </div>
      )}
    </div>
  )
}

export default connect(
  ({
    houseAndLand: { activeStageId, activeLotId },
    projectConfig: { showPrice },
    lotFilter,
    shortlist: { shortlists },
    houseAndLand,
  }: RootStateTypeExtra) => ({
    lotFilter,
    activeStageId,
    activeLotId,
    showPrice,
    shortlists,
    precinctLength: houseAndLand?.precinctList?.length,
  })
)(LotsSidePanel)
